import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"
import { collapseAllProviderGroups, expandProviderGroup, findGroupChild, toggleProviderGroup } from "../lib/access_report_groups.mjs"

// Connects to data-controller="access-report"
export default class extends Controller {
  static values = {
    partnerSlug: String,
    zip: String,
    radius: String,
    prospect: String
  }
  static targets = [
    "prospectDisplay", "prospectInput", "prospectText",
    "heroPreparedFor", "prospectEyebrow", "prospectAdd", "providerListLink", "exportLink",
    "searchContainer", "searchInput", "searchDropdown",
    "tableBody", "loadMoreContainer", "loadMoreBtn", "loadMoreRow",
    "providerRow", "showingCount", "donutChart", "pagination"
  ]

  connect() {
    this.searchTimeout = null
    this.highlightedNpi = null

    // Initialize donut charts
    this.initDonutCharts()

    // Close search dropdown on outside click
    this.boundOnOutsideClick = (e) => {
      if (this.hasSearchContainerTarget && !this.searchContainerTarget.contains(e.target)) {
        this.hideSearchDropdown()
      }
    }
    document.addEventListener("click", this.boundOnOutsideClick)

    // Listen for map provider clicks — filter table to that provider
    this.boundOnMapProviderClick = (e) => {
      this.filterTableToProvider(e.detail.npi)
    }
    document.addEventListener("access-report-map:providerClick", this.boundOnMapProviderClick)
    this.boundOnMapProviderDeselected = () => this.clearProviderFilter()
    document.addEventListener("access-report-map:providerDeselected", this.boundOnMapProviderDeselected)

    // Update hero stats and initialize donut charts when summary frame loads
    this.boundOnFrameLoad = (e) => {
      if (e.target.id === "ar-summary") {
        const tpl = e.target.querySelector("template[data-hero-stats]")
        const el = document.getElementById("ar-hero-stats")
        if (tpl && el) el.textContent = tpl.innerHTML.trim()
        this.initDonutCharts()
      }
      if (e.target.id === "ar-provider-directory") {
        this.filteredNpi = null
        this.originalShowingCount = null
        this.removeClearFilterButton()

        // Selection comes from the shared tab's server-rendered aria-current.
        const activeTab = e.target.querySelector('.ar-type-filters a[aria-current="page"]')
        if (activeTab) {
          this.syncExportFilters(activeTab.href)
          document.dispatchEvent(new CustomEvent("access-report:typeFilterChanged", {
            detail: { type: new URL(activeTab.href).searchParams.get("type") || "all" }
          }))
        }
        this.syncDirectoryProspect()
        const heading = e.target.querySelector("#ar-directory-title")
        if (this.scrollToDirectoryOnLoad && heading) {
          heading.scrollIntoView({ behavior: "smooth", block: "start" })
          this.scrollToDirectoryOnLoad = false
        }
      }
    }
    document.addEventListener("turbo:frame-load", this.boundOnFrameLoad)
  }

  disconnect() {
    document.removeEventListener("click", this.boundOnOutsideClick)
    document.removeEventListener("access-report-map:providerClick", this.boundOnMapProviderClick)
    document.removeEventListener("access-report-map:providerDeselected", this.boundOnMapProviderDeselected)
    document.removeEventListener("turbo:frame-load", this.boundOnFrameLoad)
  }

  // ── Donut Charts ───────────────────────────────────
  initDonutCharts() {
    if (!this.hasDonutChartTarget) return
    if (typeof ApexCharts === "undefined") return

    this.donutChartTargets.forEach(el => {
      const green = parseInt(el.dataset.green) || 0
      const yellow = parseInt(el.dataset.yellow) || 0
      const red = parseInt(el.dataset.red) || 0
      const total = green + yellow + red
      const centerText = el.dataset.centerText || "0%"

      if (total === 0) {
        el.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:12px;color:var(--color-text-light);">No data</div>'
        return
      }

      const chart = new ApexCharts(el, {
        chart: {
          type: "donut",
          width: 160,
          height: 160,
          sparkline: { enabled: false },
          animations: { enabled: true }
        },
        series: [green, yellow, red],
        colors: ["#4ADB54", "#E08C4C", "#E04C4C"],
        labels: ["Low risk", "Moderate risk", "High risk"],
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                name: { show: false },
                value: { show: false },
                total: {
                  show: true,
                  showAlways: true,
                  label: "Low risk",
                  fontSize: "20px",
                  fontWeight: 700,
                  fontFamily: "var(--f-sans)",
                  color: "#1E2046",
                  formatter: () => centerText
                }
              }
            }
          }
        },
        legend: { show: false },
        tooltip: { enabled: true },
        dataLabels: { enabled: false },
        stroke: { width: 2, colors: ["#fff"] },
        states: {
          hover: { filter: { type: "none" } },
          active: { filter: { type: "none" } }
        }
      })
      chart.render()
    })
  }

  // ── Radius Selector ────────────────────────────────
  changeRadius(e) {
    const form = e.target.closest("form")
    if (form) form.requestSubmit()
  }

  // ── Providers-analyzed Selector ────────────────────
  // changeRadius resolves the form from the event target, so it submits
  // whichever selector fired it rather than the radius form specifically.
  changeProviderLimit(e) {
    this.changeRadius(e)
  }

  // ── Prospect Name ──────────────────────────────────
  editProspectName() {
    if (!this.hasProspectInputTarget || !this.hasProspectDisplayTarget) return
    if (this.hasProspectEyebrowTarget) this.prospectEyebrowTarget.classList.remove("hidden")
    if (this.hasProspectAddTarget) this.prospectAddTarget.classList.add("hidden")
    this.prospectDisplayTarget.classList.add("hidden")
    this.prospectInputTarget.classList.remove("hidden")
    this.prospectInputTarget.focus()
  }

  onProspectInput() {
    const name = this.prospectInputTarget.value.trim()

    // Update prospect text display in real-time
    if (this.hasProspectTextTarget) {
      this.prospectTextTarget.textContent = name || "Add prospect name..."
      if (!this.hasProspectEyebrowTarget) this.prospectTextTarget.style.color = name ? "var(--color-text)" : "var(--color-text-light)"
    }

    // Update hero "Prepared for" in real-time
    if (this.hasHeroPreparedForTarget) {
      this.heroPreparedForTarget.textContent = name ? `Prepared for ${name}` : ""
      this.heroPreparedForTarget.classList.toggle("hidden", !name)
    }

  }

  saveProspectName() {
    if (!this.hasProspectInputTarget || !this.hasProspectDisplayTarget) return
    const name = this.prospectInputTarget.value.trim()
    this.prospectDisplayTarget.classList.remove("hidden")
    this.prospectInputTarget.classList.add("hidden")

    if (this.hasProspectTextTarget) {
      this.prospectTextTarget.textContent = name || "Add prospect name..."
      if (!this.hasProspectEyebrowTarget) this.prospectTextTarget.style.color = name ? "var(--color-text)" : "var(--color-text-light)"
    }

    if (this.hasProspectEyebrowTarget) this.prospectEyebrowTarget.classList.toggle("hidden", !name)
    if (this.hasProspectAddTarget) this.prospectAddTarget.classList.toggle("hidden", !!name)
    this.prospectValue = name
    this.syncDirectoryProspect()
    this.providerListLinkTargets.concat(this.exportLinkTargets).forEach(link => {
      const linkUrl = new URL(link.href)
      if (name) linkUrl.searchParams.set("prospect", name)
      else linkUrl.searchParams.delete("prospect")
      link.href = linkUrl.toString()
    })

    // Update URL query param
    const url = new URL(window.location.href)
    if (name) {
      url.searchParams.set("prospect", name)
    } else {
      url.searchParams.delete("prospect")
    }
    window.history.replaceState({}, "", url.toString())

    // Sync hidden prospect field in zip form so name survives form submission
    const zipForm = this.element.querySelector("form")
    if (zipForm) {
      let hidden = zipForm.querySelector('input[name="prospect"]')
      if (name) {
        if (!hidden) {
          hidden = document.createElement("input")
          hidden.type = "hidden"
          hidden.name = "prospect"
          zipForm.prepend(hidden)
        }
        hidden.value = name
      } else if (hidden) {
        hidden.remove()
      }
    }

  }

  prospectKeydown(e) {
    if (e.key === "Enter") {
      e.preventDefault()
      this.saveProspectName()
    }
  }

  // Directory links are server-rendered; keep them in sync if the customer name
  // is edited after a frame loads, including every pagination destination.
  syncDirectoryProspect() {
    this.element.querySelectorAll('#ar-provider-directory a[href]').forEach(link => {
      const url = new URL(link.href)
      if (this.prospectValue) url.searchParams.set("prospect", this.prospectValue)
      else url.searchParams.delete("prospect")
      link.href = url.toString()
    })
  }

  syncExportFilters(directoryHref) {
    if (!this.hasExportLinkTarget) return
    const directoryUrl = new URL(directoryHref)
    const exportUrl = new URL(this.exportLinkTarget.href)
    for (const key of ["type", "risk"]) {
      exportUrl.searchParams.set(key, directoryUrl.searchParams.get(key) || "all")
    }
    this.exportLinkTarget.href = exportUrl.toString()
  }

  paginateDirectory(event) {
    if (event.target.closest("a") && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      this.scrollToDirectoryOnLoad = true
    }
  }

  // ── Provider Search ────────────────────────────────
  onSearchInput() {
    clearTimeout(this.searchTimeout)
    const query = this.searchInputTarget.value.trim()

    if (query.length < 2) {
      this.hideSearchDropdown()
      return
    }

    this.searchTimeout = setTimeout(() => this.fetchSearchResults(query), 150)
  }

  onSearchFocus() {
    const query = this.searchInputTarget.value.trim()
    if (query.length >= 2) {
      this.fetchSearchResults(query)
    }
  }

  async fetchSearchResults(query) {
    try {
      let url = `/partners/${this.partnerSlugValue}/access_report/provider_search?q=${encodeURIComponent(query)}&zip=${this.zipValue}`
      if (this.radiusValue) url += `&radius=${this.radiusValue}`
      const response = await fetch(url, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" }
      })
      const html = await response.text()
      this.renderSearchResults(html)
    } catch (e) {
      console.error("Search failed:", e)
    }
  }

  renderSearchResults(html) {
    if (!this.hasSearchDropdownTarget) return

    if (!html || html.trim().length === 0) {
      this.hideSearchDropdown()
      return
    }

    this.searchDropdownTarget.innerHTML = html
    this.searchDropdownTarget.classList.remove("hidden")
  }

  selectSearchResult(e) {
    const resultEl = e.currentTarget
    const npi = resultEl.dataset.npi

    this.hideSearchDropdown()
    this.searchInputTarget.value = ""

    // A collapsed sibling has no pin of its own: the grouped GeoJSON carries one
    // feature per representative (see NavigationAccessService.build_geojson), so
    // the map has to be told the group's NPI or its highlight finds nothing and
    // clears the ring. The table still filters and opens on the sibling itself.
    const child = findGroupChild(this.element, npi)
    const mapNpi = child ? child.groupId : npi
    // And the pan has to follow the same pin. A sibling's own NPPES
    // coordinates can be a block (or a county) away from the representative's,
    // so panning there would leave the pin we just ringed off screen at the
    // zoom this selection forces — the same "nothing is highlighted" symptom.
    const { lat, lng } = child
      ? this.rowCoordinates(mapNpi)
      : { lat: parseFloat(resultEl.dataset.lat), lng: parseFloat(resultEl.dataset.lng) }

    // Filter table to only show the selected provider
    this.filterTableToProvider(npi)

    // Dispatch event for map to highlight + pan
    this.dispatch("providerSelected", { detail: { npi: mapNpi, lat, lng } })
  }

  // A directory row's own coordinates, as _provider_row.html.erb rendered them.
  // NaN when the row isn't in the DOM or has none, which the map reads as
  // "highlight, but don't pan anywhere" rather than panning to the wrong place.
  rowCoordinates(npi) {
    const row = this.element.querySelector(`tr[data-npi="${npi}"]`)

    return { lat: parseFloat(row?.dataset.lat), lng: parseFloat(row?.dataset.lng) }
  }

  filterTableToProvider(npi) {
    // Guard: if the provider row isn't in the DOM, fall back to highlight behavior
    const matchedRow = this.element.querySelector(`tr[data-npi="${npi}"]`)
    if (!matchedRow) {
      // The search dropdown comes from NPPES, so the npi can be one this
      // directory only renders as a sibling collapsed inside a group. The table
      // filters by top-level row, so filter to the group's row and then open it
      // on the sibling the user actually picked.
      const child = findGroupChild(this.element, npi)
      if (child) {
        this.filterTableToProvider(child.groupId)
        // Re-stated AFTER the group opens: the filter pass above counted the
        // one top-level row it left visible, and opening the group puts the
        // sibling rows back on screen alongside it.
        this.setFilteredCount(this.revealGroupChild(child))
        return
      }

      this.highlightProviderInTable(npi)
      return
    }

    this.filteredNpi = npi

    // Sibling rows carry no data-npi, so the pass below can't hide them.
    collapseAllProviderGroups(this.element)

    // Hide all rows that don't match
    this.element.querySelectorAll("tr[data-npi]").forEach(row => {
      const detail = this.element.querySelector(`tr[data-npi-detail="${row.dataset.npi}"]`)
      if (row.dataset.npi === npi) {
        row.classList.remove("hidden")
        row.classList.add("highlighted")
        if (detail) {
          detail.classList.remove("hidden")
          detail.classList.add("highlighted")
          row.classList.add("expanded")
          const chevron = row.querySelector("[data-chevron]")
          if (chevron) chevron.style.transform = "rotate(90deg)"
        }
      } else {
        row.classList.add("hidden")
        if (detail) detail.classList.add("hidden")
      }
    })

    // Hide page navigation while filtered to one provider
    if (this.hasPaginationTarget) this.paginationTarget.classList.add("hidden")

    // Legacy append pagination
    if (this.hasLoadMoreContainerTarget) this.loadMoreContainerTarget.classList.add("hidden")
    if (this.hasLoadMoreRowTarget) this.loadMoreRowTarget.classList.add("hidden")

    // Update showing count
    this.setFilteredCount()

    // Show clear-filter button
    this.showClearFilterButton()

    // Scroll to the matched row
    matchedRow.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  // The filter header has to describe the rows that are actually on screen.
  // Filtering to one provider normally leaves exactly one row, but selecting a
  // collapsed sibling filters to its group's row and then re-opens the group,
  // so the representative's siblings are visible too — all one facility, but
  // more rows than a bare "Showing 1 provider" claims. `relatedNpiCount` is how
  // many sibling rows that left showing (0 for an ordinary single-row filter).
  // See docs/access_report.md.
  setFilteredCount(relatedNpiCount = 0) {
    if (!this.hasShowingCountTarget) return

    this.originalShowingCount = this.originalShowingCount || this.showingCountTarget.textContent
    const related = relatedNpiCount > 0
      ? ` · ${relatedNpiCount} related ${relatedNpiCount === 1 ? "NPI" : "NPIs"}`
      : ""
    this.showingCountTarget.textContent = `Showing 1 provider${related} (filtered) · `
  }

  clearProviderFilter() {
    this.filteredNpi = null

    // Groups come back collapsed rather than in whatever state the filter
    // interrupted, which is also the state a freshly rendered directory is in.
    collapseAllProviderGroups(this.element)

    // Show all rows again
    this.element.querySelectorAll("tr[data-npi]").forEach(row => {
      row.classList.remove("hidden", "highlighted")
      const detail = this.element.querySelector(`tr[data-npi-detail="${row.dataset.npi}"]`)
      if (detail) {
        detail.classList.add("hidden")
        detail.classList.remove("highlighted")
        row.classList.remove("expanded")
        const chevron = row.querySelector("[data-chevron]")
        if (chevron) chevron.style.transform = ""
      }
    })

    if (this.hasPaginationTarget) this.paginationTarget.classList.remove("hidden")

    // Restore legacy append pagination
    if (this.hasLoadMoreContainerTarget) this.loadMoreContainerTarget.classList.remove("hidden")
    if (this.hasLoadMoreRowTarget) this.loadMoreRowTarget.classList.remove("hidden")

    // Restore showing count
    if (this.hasShowingCountTarget && this.originalShowingCount) {
      this.showingCountTarget.textContent = this.originalShowingCount
      this.originalShowingCount = null
    }

    // Remove clear-filter button
    this.removeClearFilterButton()
    this.dispatch("providerSelectionCleared")
  }

  showClearFilterButton() {
    this.removeClearFilterButton()
    const btn = document.createElement("button")
    btn.id = "ar-clear-filter-btn"
    btn.textContent = "✕ Clear filter"
    btn.style.cssText = "font-size: 12px; font-weight: 600; padding: 4px 12px; border-radius: 8px; border: 1px solid var(--color-border); background: #fff; color: var(--color-primary); cursor: pointer;"
    btn.addEventListener("click", () => this.clearProviderFilter())

    const slot = document.getElementById("ar-clear-filter-slot")
    if (slot) {
      slot.appendChild(btn)
    }
  }

  removeClearFilterButton() {
    const existing = document.getElementById("ar-clear-filter-btn")
    if (existing) existing.remove()
  }

  hideSearchDropdown() {
    if (this.hasSearchDropdownTarget) {
      this.searchDropdownTarget.classList.add("hidden")
    }
  }

  // ── Table Row Expand/Collapse ──────────────────────
  toggleRow(e) {
    const row = e.currentTarget
    const npi = row.dataset.npi
    if (!npi) return

    const detailRow = this.element.querySelector(`tr[data-npi-detail="${npi}"]`)
    if (!detailRow) return

    const isExpanded = !detailRow.classList.contains("hidden")
    if (isExpanded) {
      detailRow.classList.add("hidden")
      row.classList.remove("expanded")
      const chevron = row.querySelector("[data-chevron]")
      if (chevron) chevron.style.transform = ""
    } else {
      detailRow.classList.remove("hidden")
      row.classList.add("expanded")
      const chevron = row.querySelector("[data-chevron]")
      if (chevron) chevron.style.transform = "rotate(90deg)"
    }
  }

  // ── EIN Group Accordion ────────────────────────────
  // Reveals the sibling NPIs the server already rendered beneath a grouped row
  // (see docs/access_report.md). The action is bound with `:stop` so expanding
  // a group doesn't also toggle the row's own detail drawer.
  toggleGroup(e) {
    toggleProviderGroup(this.element, e.currentTarget)
  }

  // Bound with `:stop` on cells holding their own interactive content (the
  // admin Fallback Networks avatar stack), so pinning a network's flyout
  // doesn't also expand or collapse the row's detail drawer.
  keepRowClosed() {}

  // Opens the group a searched-for sibling NPI lives in and marks the sibling
  // itself, so selecting a collapsed NPI lands on that NPI's row rather than
  // just on its parent's. Returns how many sibling rows the group now shows,
  // which is what the filter header has to count alongside the
  // representative's own row.
  revealGroupChild(child) {
    expandProviderGroup(this.element, child.groupId)
    child.row.classList.add("highlighted")
    if (child.row.scrollIntoView) child.row.scrollIntoView({ behavior: "smooth", block: "center" })

    return this.element.querySelectorAll(`tr[data-group-child="${child.groupId}"]`).length
  }

  highlightProviderInTable(npi) {
    // Skip highlight if a provider filter is active to avoid orphaned detail rows
    if (this.filteredNpi) return

    // Remove previous highlight
    this.element.querySelectorAll("tr.highlighted").forEach(r => r.classList.remove("highlighted"))

    const row = this.element.querySelector(`tr[data-npi="${npi}"]`)
    const detailRow = this.element.querySelector(`tr[data-npi-detail="${npi}"]`)

    // A collapsed sibling has no row of its own to highlight: open its group and
    // highlight the sibling itself rather than doing nothing.
    if (!row) {
      const child = findGroupChild(this.element, npi)
      if (child) this.revealGroupChild(child)
      return
    }

    row.classList.add("highlighted")
    // Expand the detail row
    if (detailRow) {
      detailRow.classList.remove("hidden")
      detailRow.classList.add("highlighted")
      row.classList.add("expanded")
      const chevron = row.querySelector("[data-chevron]")
      if (chevron) chevron.style.transform = "rotate(90deg)"
    }
    row.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  // ── Load More Pagination ───────────────────────────
  async loadMore(e) {
    const btn = e.currentTarget
    const url = btn.dataset.url
    if (!url) return

    btn.disabled = true
    btn.textContent = "Loading..."

    try {
      const response = await fetch(url, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" }
      })

      // The snapshot was republished since the directory rendered (409): its rows
      // are sorted over a different provider set, so appending this page beneath
      // the ones already on screen would duplicate or skip providers. Reload the
      // directory from page 1 of the current snapshot instead.
      if (response.status === 409) {
        this.reloadProviderDirectory()
        return
      }

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`)
      }

      const html = await response.text()

      // Remove load more container/row
      if (this.hasLoadMoreContainerTarget) {
        this.loadMoreContainerTarget.remove()
      }
      if (this.hasLoadMoreRowTarget) {
        this.loadMoreRowTarget.remove()
      }

      // Append new rows
      if (this.hasTableBodyTarget) {
        this.tableBodyTarget.insertAdjacentHTML("beforeend", html)
      }
    } catch (err) {
      console.error("Load more failed:", err)
      btn.disabled = false
      btn.textContent = "Retry"
    }
  }

  // Re-renders the provider directory frame from its own src, which is page 1 of
  // whatever snapshot is current.
  reloadProviderDirectory() {
    const frame = document.getElementById("ar-provider-directory")
    if (!frame) return

    if (typeof frame.reload === "function") {
      frame.reload()
      return
    }

    const src = frame.getAttribute("src")
    if (!src) return
    frame.removeAttribute("src")
    frame.setAttribute("src", src)
  }

  // ── Utility ────────────────────────────────────────
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
